import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSave, FiUser, FiShoppingBag, FiCreditCard, FiMail, FiBell, FiLock } from 'react-icons/fi';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    businessName: '',
    email: '',
    phone: '',
    address: '',
    currency: 'USD',
    taxRate: '',
    receiptFooter: '',
    enableNotifications: true,
    enableEmailAlerts: false,
    lowStockThreshold: 10,
    autoBackup: true
  });
  const { currentUser, selectedHierarchy } = useAuth();

  useEffect(() => {
    loadSettings();
  }, [selectedHierarchy]);

  const loadSettings = async () => {
    if (!selectedHierarchy?.outlet?.id) return;

    try {
      const settingsDoc = await getDoc(doc(db, 'settings', selectedHierarchy.outlet.id));
      if (settingsDoc.exists()) {
        setSettings({ ...settings, ...settingsDoc.data() });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedHierarchy?.outlet?.id) {
      alert('Please select a business outlet');
      return;
    }

    try {
      setSaving(true);
      await updateDoc(doc(db, 'settings', selectedHierarchy.outlet.id), {
        ...settings,
        updatedAt: new Date(),
        updatedBy: currentUser?.email || 'system'
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const tabs = [
    { id: 'general', label: 'General', icon: FiShoppingBag },
    { id: 'profile', label: 'Profile', icon: FiUser },
    { id: 'payment', label: 'Payment', icon: FiCreditCard },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'security', label: 'Security', icon: FiLock }
  ];

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your application preferences</p>
      </div>

      <div className="row">
        {/* Sidebar Tabs */}
        <div className="col-md-3 mb-4">
          <div className="card">
            <div className="list-group list-group-flush">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    className={`list-group-item list-group-item-action d-flex align-items-center ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon className="me-2" size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="col-md-9">
          <div className="card">
            <div className="card-body">
              {/* General Settings */}
              {activeTab === 'general' && (
                <div>
                  <h5 className="mb-4">General Settings</h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Business Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.businessName}
                        onChange={(e) => handleChange('businessName', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Currency</label>
                      <select
                        className="form-select"
                        value={settings.currency}
                        onChange={(e) => handleChange('currency', e.target.value)}
                      >
                        <option value="USD">USD - US Dollar</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="GBP">GBP - British Pound</option>
                        <option value="INR">INR - Indian Rupee</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={settings.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={settings.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={settings.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Tax Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={settings.taxRate}
                        onChange={(e) => handleChange('taxRate', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Low Stock Threshold</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.lowStockThreshold}
                        onChange={(e) => handleChange('lowStockThreshold', e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Receipt Footer Text</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={settings.receiptFooter}
                        onChange={(e) => handleChange('receiptFooter', e.target.value)}
                        placeholder="Thank you for your business!"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Settings */}
              {activeTab === 'profile' && (
                <div>
                  <h5 className="mb-4">Profile Settings</h5>
                  <div className="text-center mb-4">
                    <div className="avatar-xl bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '100px', height: '100px', fontSize: '2rem' }}>
                      {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <p className="mt-3 mb-0 fw-semibold">{currentUser?.email || 'User'}</p>
                    <p className="text-muted small">{selectedHierarchy?.outlet?.name || 'No outlet selected'}</p>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Display Name</label>
                      <input type="text" className="form-control" placeholder="Enter your name" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Language</label>
                      <select className="form-select">
                        <option>English</option>
                        <option>Spanish</option>
                        <option>French</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Time Zone</label>
                      <select className="form-select">
                        <option>UTC</option>
                        <option>EST</option>
                        <option>PST</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date Format</label>
                      <select className="form-select">
                        <option>MM/DD/YYYY</option>
                        <option>DD/MM/YYYY</option>
                        <option>YYYY-MM-DD</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Settings */}
              {activeTab === 'payment' && (
                <div>
                  <h5 className="mb-4">Payment Settings</h5>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-check form-switch mb-3">
                        <input className="form-check-input" type="checkbox" id="cashPayment" defaultChecked />
                        <label className="form-check-label" htmlFor="cashPayment">
                          Enable Cash Payments
                        </label>
                      </div>
                      <div className="form-check form-switch mb-3">
                        <input className="form-check-input" type="checkbox" id="cardPayment" defaultChecked />
                        <label className="form-check-label" htmlFor="cardPayment">
                          Enable Card Payments
                        </label>
                      </div>
                      <div className="form-check form-switch mb-3">
                        <input className="form-check-input" type="checkbox" id="digitalWallet" />
                        <label className="form-check-label" htmlFor="digitalWallet">
                          Enable Digital Wallets
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Payment Gateway</label>
                      <select className="form-select">
                        <option>Stripe</option>
                        <option>PayPal</option>
                        <option>Square</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">API Key</label>
                      <input type="password" className="form-control" placeholder="Enter API key" />
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {activeTab === 'notifications' && (
                <div>
                  <h5 className="mb-4">Notification Preferences</h5>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="enableNotifications"
                      checked={settings.enableNotifications}
                      onChange={(e) => handleChange('enableNotifications', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="enableNotifications">
                      Enable Push Notifications
                    </label>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="enableEmailAlerts"
                      checked={settings.enableEmailAlerts}
                      onChange={(e) => handleChange('enableEmailAlerts', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="enableEmailAlerts">
                      Enable Email Alerts
                    </label>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="lowStock" defaultChecked />
                    <label className="form-check-label" htmlFor="lowStock">
                      Low Stock Alerts
                    </label>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="newOrders" defaultChecked />
                    <label className="form-check-label" htmlFor="newOrders">
                      New Order Notifications
                    </label>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="autoBackup"
                      checked={settings.autoBackup}
                      onChange={(e) => handleChange('autoBackup', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="autoBackup">
                      Automatic Backup Notifications
                    </label>
                  </div>
                </div>
              )}

              {/* Security */}
              {activeTab === 'security' && (
                <div>
                  <h5 className="mb-4">Security Settings</h5>
                  <div className="row g-3">
                    <div className="col-12">
                      <h6>Change Password</h6>
                      <p className="text-muted small">Update your password to keep your account secure</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Current Password</label>
                      <input type="password" className="form-control" />
                    </div>
                    <div className="col-md-6"></div>
                    <div className="col-md-6">
                      <label className="form-label">New Password</label>
                      <input type="password" className="form-control" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Confirm Password</label>
                      <input type="password" className="form-control" />
                    </div>
                    <div className="col-12">
                      <button className="btn btn-primary">Update Password</button>
                    </div>
                    <div className="col-12 mt-4">
                      <hr />
                      <h6 className="mt-4">Two-Factor Authentication</h6>
                      <p className="text-muted small">Add an extra layer of security to your account</p>
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" id="2fa" />
                        <label className="form-check-label" htmlFor="2fa">
                          Enable Two-Factor Authentication
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-4 pt-3 border-top">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <FiSave className="me-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
